import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function IslamicGuide64() {
  const contentRef = useRef(null);
  const titleText =
    "Bitcoin's Pseudonymity: A Modern Application of Islamic Privacy";
  const links = {
    prev: "/islamicguide-63",
    index: "/indexislamic-guide",
    next: "/islamicguide-65",
  };

  const questions = [
    {
      text: "1. In Islamic teachings, why is privacy considered an essential right?",
      options: [
        {
          value: "A",
          label: "A. Because it allows people to avoid paying taxes",
        },
        {
          value: "B",
          label:
            "B. Because it protects dignity, trust, and prevents unnecessary harm",
        },
        {
          value: "C",
          label: "C. Because it promotes secrecy in financial dealings",
        },
        { value: "D", label: "D. Because it prevents trade with strangers" },
      ],
      correct: "B",
    },
    {
      text: "2. How does Bitcoin’s pseudonymous ledger align with Islamic values of privacy?",
      options: [
        {
          value: "A",
          label: "A. It completely hides all transactions from the public",
        },
        {
          value: "B",
          label: "B. It links real-world identities to every transaction",
        },
        {
          value: "C",
          label:
            "C. It records transactions openly but does not directly reveal personal identities",
        },
        {
          value: "D",
          label:
            "D. It requires government approval before transactions can be made",
        },
      ],
      correct: "C",
    },
    {
      text: "3. What Islamic principle does using secure digital practices (like encrypted wallets and private keys) best represent?",
      options: [
        { value: "A", label: "A. Zakat (charity)" },
        { value: "B", label: "B. Amanah (trust)" },
        { value: "C", label: "C. Shura (consultation)" },
        { value: "D", label: "D. Sadaqah (voluntary giving)" },
      ],
      correct: "B",
    },
    {
      text: "4. Why might Bitcoin be a valuable tool for Muslims in the age of surveillance?",
      options: [
        { value: "A", label: "A. It guarantees wealth without effort" },
        {
          value: "B",
          label:
            "B. It ensures financial privacy and protects individuals from profiling and exploitation",
        },
        {
          value: "C",
          label: "C. It is endorsed by every government worldwide",
        },
        {
          value: "D",
          label: "D. It eliminates the need to follow Islamic financial ethics",
        },
      ],
      correct: "B",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);
  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={64}
        timerMinutes={60}
        currentLevel={5}
        unlockedBadges={9}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 64</b>! Today we will reflect on how privacy is a
        deeply rooted principle in Islam, woven into the values of dignity,
        respect, and the protection of individual rights. In an age where every
        action leaves a digital footprint and where corporations, and malicious
        actors increasingly monitor financial activity, Muslims face the
        challenge of living faithfully while navigating modern technology. This
        lesson explores how the timeless Islamic principles of privacy and trust
        translate into the digital era, with a special focus on Bitcoin as a
        tool that allows Muslims to preserve financial confidentiality. By
        understanding Bitcoin’s pseudonymous ledger and how it aligns with
        Islamic ethics, learners will see how modern technology can be harnessed
        responsibly to uphold Islamic values in today’s surveillance-driven
        world.
        <br />
        <br />
        <b>The Islamic Ethical Framework of Privacy</b>
        <br />
        <br />
        Privacy is a cornerstone of Islamic ethics, deeply embedded in the
        Qur’an and the teachings of the Prophet Muhammad (peace be upon him). In
        Islam, privacy is not merely about secrecy; it is about maintaining
        trust, dignity, and moral integrity in social, professional, and
        spiritual interactions. The Qur’an repeatedly underscores the importance
        of respecting boundaries and safeguarding personal information. For
        instance, Surah Al-Hujurat (49:12) prohibits spying, backbiting, and
        spreading suspicion among people. The Prophet Muhammad (peace be upon
        him) reinforced this by instructing:
        <br />
        "Do not spy, and do not seek out others’ faults. Do not compete against
        one another, do not envy one another, do not hate one another, and do
        not turn away from one another. Be they, O servants of Allah, brothers."
        <br />
        This instruction is more than a social guideline; it is a moral
        imperative. Islam views acts such as backbiting (ghibah) or exposing
        someone’s personal affairs without consent as serious ethical
        violations. The Qur’an describes backbiting as akin to “eating the flesh
        of your dead brother,” highlighting the gravity of breaching someone’s
        privacy.
        <br />
        Moreover, the principle of Amanah (trust) emphasizes that any
        information entrusted to a person is sacred and cannot be misused or
        disclosed without consent. The Prophet Muhammad (peace be upon him)
        said:
        <br />
        "When a man quotes a saying and then goes away, it is trust."
        <br />
        This underscores that even verbal communication carries an ethical
        expectation of confidentiality. Islam’s ethical framework establishes a
        robust foundation for privacy, emphasizing protection against harm
        (Darar), honesty, and the preservation of social trust. It teaches that
        the individual’s personal, financial, and religious information is not
        merely a private matter but a trust from Allah that must be protected.
        <br />
        In today’s modern world, where digital surveillance, social media
        tracking, and data breaches are rampant, these ethical principles are
        more relevant than ever. For Muslims, protecting one’s personal and
        financial information is not only a matter of security but a fulfillment
        of a sacred trust. Islamic guidance urges believers to adopt behaviors
        that prevent harm, maintain integrity, and respect the privacy of
        oneself and others.
        <br />
        <br />
        <b>The Digital Landscape and Threats to Privacy</b>
        <br />
        <br />
        The rapid digitization of society has created an environment where
        personal and financial data are constantly at risk. Corporations, and
        malicious actors collect and monitor personal data through a variety of
        methods including cookies, social media tracking, biometric scanning,
        and sophisticated data analytics. Social media platforms such as
        Facebook, Instagram, and Twitter store vast amounts of personal
        information, ranging from messages and photos to location data and
        behavioral patterns.
        <br />
        Unfortunately, much of this data is acquired without the informed
        consent of the individual. Companies often use algorithms to build
        exhaustive profiles for marketing purposes, political influence, or even
        financial gain. Even when legal frameworks allow such practices, they
        raise serious ethical questions about transparency, consent, and the
        right to privacy. For Muslims, whose faith emphasizes the inviolability
        of personal information and the obligation to prevent harm, this
        presents a significant challenge.
        <br />
        Digital threats are not limited to corporations. Data breaches, phishing
        attacks, and cybercrimes pose real risks to identity, finances, and
        personal safety. Financial information, in particular, is highly
        sensitive. Unauthorized access to bank accounts, charitable donations
        (such as zakat), or transaction histories can lead to financial fraud,
        social profiling, and even targeted persecution. Islam teaches that such
        acts of deception and theft are forbidden, and the safeguarding of
        financial information is not only a legal concern but a moral duty.
        <br />
        Online identity theft is a growing concern, as stolen personal
        information can be used to impersonate individuals, gain unauthorized
        access to accounts, or exploit religious affiliations. For Muslims, the
        stakes are even higher: sensitive religious practices, charitable
        contributions, or pilgrimages could be exposed and misused. The Qur’an
        explicitly prohibits consuming the wealth of others unjustly (Surah
        Al-Baqarah 2:188), highlighting the sanctity of financial information.
        Ethical behavior in the digital age requires vigilance, proactive
        protection, and adherence to principles of honesty, trust, and non-harm.
        <br />
        Islamic ethical principles translate directly into practical digital
        habits: using strong passwords, enabling two-factor authentication
        (2FA), avoiding suspicious websites, and limiting personal information
        shared online. Muslims are reminded that privacy is a right, a sacred
        trust, and a protection against harm. Ethical considerations, rooted in
        Amanah and Darar, form the foundation for responsible behavior in the
        digital sphere.
        <br />
        <br />
        <b>Bitcoin as a Tool for Financial Privacy</b>
        <br />
        <br />
        In the age of digital finance, Bitcoin offers a practical solution for
        Muslims to uphold privacy while engaging in financial transactions.
        Unlike traditional banking systems where transactions are linked to
        identifiable individuals, Bitcoin operates on a pseudonymous ledger.
        Each transaction is recorded on a public blockchain, but the
        participants’ identities are not inherently revealed. Instead, users
        interact through cryptographic addresses i.e unique alphanumeric strings
        that serve as identifiers without exposing personal details.
        <br />
        The pseudonymity of Bitcoin aligns closely with Islamic principles of
        privacy and Amanah. By using Bitcoin, Muslims can transact without
        unnecessarily exposing sensitive financial data to third parties such as
        banks, or corporations. This reduces the risk of financial exploitation,
        identity theft, or profiling based on religious practices and charitable
        contributions.
        <br /> To maximize privacy in Bitcoin transactions, users must adopt
        practices consistent with both Islamic ethics and digital security:
        <ul>
          <li>
            Use New Addresses for Each Transaction: Reusing addresses makes it
            easier to link multiple transactions to a single user. Generating a
            fresh address for each transaction preserves confidentiality.
          </li>
          <li>
            Avoid Linking Personal Information: Do not attach names, phone
            numbers, or email addresses to Bitcoin transactions. Linking
            personal details undermines the pseudonymous nature of the
            blockchain.
          </li>
          <li>
            Leverage Wallet Privacy Features: Some wallets, like Wasabi or
            Samourai, offer features such as coin mixing or coinjoin, which
            further obscure transaction paths and protect user privacy.
          </li>
          <li>
            Consider Network Privacy Tools: Using Tor or VPN services when
            transacting can shield IP addresses, preventing correlation attacks
            that might compromise privacy.
          </li>
        </ul>
        These practices exemplify the ethical responsibility Muslims have in
        protecting their financial data. Just as Islam teaches honesty, respect
        for others, and avoidance of harm, these digital practices help ensure
        that financial dealings remain confidential, secure, and ethical.
        <br />
        <br />
        While Bitcoin offers privacy, it also demands ethical responsibility.
        Islam emphasizes that trust must not be violated. Therefore, while
        transactions can be private, they must also adhere to principles of
        honesty and integrity. Using Bitcoin to hide illicit activity or defraud
        others is strictly prohibited. Privacy in finance is a means to protect
        oneself and others, not a tool for wrongdoing.
        <br />
        Islamic ethics also require that Muslims maintain transparency where
        necessary, such as in communal charitable donations. Using Bitcoin
        responsibly allows Muslims to fulfill obligations such as zakat while
        protecting sensitive financial information from public exposure or
        misuse.
        <br />
        <br />
        <b>
          Practical Steps for Muslims to Secure Financial Privacy with Bitcoin
        </b>
        <br />
        <br />
        Adopting Bitcoin responsibly involves a combination of technical
        knowledge, ethical guidance, and ongoing vigilance. By integrating
        Islamic ethical principles with modern privacy tools, Muslims can secure
        their financial life while honoring their faith.
        <br />
        <br />
        Technical Measures for Privacy
        <ul>
          <li>
            Secure Wallets: Choose wallets that prioritize privacy and security.
            Hardware wallets, for instance, provide offline storage that reduces
            the risk of hacking.
          </li>
          <li>
            Transaction Obfuscation: Use coin-mixing services or privacy wallets
            to ensure transactions cannot be easily traced on the blockchain.
          </li>
          <li>
            IP Anonymity: Access the Bitcoin network using Tor or a reputable
            VPN to prevent linking transactions to physical locations.
          </li>
          <li>
            Multi-Signature Wallets: Consider multi-signature wallets for added
            security, which require multiple private keys to authorize a
            transaction. This ensures shared responsibility and safeguards funds
            against misuse.
          </li>
          <li>
            Regular Backups and Encryption: Backup wallet files securely and
            encrypt them to prevent data loss or unauthorized access.
          </li>
        </ul>
        Ethical and Religious Guidelines
        <ul>
          <li>
            Avoid Harm (Darar): Ensure Bitcoin use does not harm others
            financially, socially, or morally.
          </li>
          <li>
            Protect Trust (Amanah): Keep all private financial data secure,
            respecting the trust others place in you when sending or receiving
            funds.
          </li>
          <li>
            Transparent Intentions: Conduct transactions with clear, lawful
            intentions. Avoid using Bitcoin to bypass legitimate regulations or
            exploit others.
          </li>
          <li>
            Avoid Oversharing: Limit disclosure of transaction amounts,
            addresses, or wallets to only trusted parties when necessary.
          </li>
          <li>
            Continuous Learning: Stay informed about developments in blockchain
            privacy technologies and legal frameworks to maintain both security
            and compliance.
          </li>
        </ul>
        Bitcoin represents a powerful tool for Muslims to uphold ethical
        financial privacy in an age of pervasive surveillance. By integrating
        the principles of Amanah, Darar, and ethical responsibility with
        privacy-focused technologies, Muslims can achieve a balance between
        leveraging modern advancements and preserving the moral and spiritual
        values central to Islam. Each decision in managing digital assets, from
        wallet selection to transaction execution, becomes an act of upholding
        divine trust, protecting oneself, and contributing to a culture of
        responsible privacy.
        <br />
        Islam’s teachings on privacy, trust, and non-harm provide a
        comprehensive framework for protecting personal and financial
        information. In the modern age of digital surveillance, data breaches,
        and cybercrime, these principles are more relevant than ever. Bitcoin,
        with its pseudonymous ledger, offers a practical means for Muslims to
        safeguard financial privacy while remaining compliant with ethical and
        religious obligations. By adopting strong digital security practices,
        leveraging privacy-focused wallets, understanding legal rights, and
        following the moral imperatives of Amanah and Darar, Muslims can
        navigate the digital financial landscape with integrity and confidence.
        Financial privacy is not merely a convenience; it is a sacred trust that
        safeguards dignity, autonomy, and community cohesion. Muslims are
        encouraged to view Bitcoin not just as a technological tool but as a
        means to embody the timeless Islamic values of trust, privacy, and
        ethical responsibility in an interconnected world. Protecting personal
        data, preserving financial confidentiality, and avoiding harm in the
        digital sphere ensures that believers remain faithful stewards of their
        own and others’ trust, aligning modern financial practices with the
        enduring moral teachings of Islam.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today we explored how Islam’s timeless principles of privacy, trust
        (Amanah), and the prevention of harm (Darar) directly translate into the
        need for protecting digital and financial privacy in today’s
        surveillance-driven age. It highlights that just as the Qur’an and
        Sunnah safeguard personal dignity and confidentiality, Muslims have a
        duty to extend these values into their digital lives. Bitcoin, with its
        pseudonymous ledger, emerges as a practical tool that allows Muslims to
        preserve financial privacy while engaging in lawful transactions,
        protecting themselves from exploitation, profiling, and data misuse. By
        combining strong technical practices like secure wallets, encrypted
        communications, and anonymity tools with ethical Islamic guidance,
        Muslims can responsibly navigate the digital financial landscape.
        Ultimately, Bitcoin is presented not just as technology, but as a means
        for Muslims to embody Islamic values of trust, integrity, and respect
        for privacy in the modern world.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In our next lesson, Day 65, we will turn our attention to one of the
        gravest prohibitions in Islam: Riba. We will study how the global fiat
        monetary system is entirely built upon usury, and how it traps nations
        and individuals in cycles of debt and exploitation. At the same time, we
        will uncover how Bitcoin provides Muslims with a powerful alternative
        i.e a monetary network that is free of debt, free of inflation, and
        built on fairness. By examining the Qur’anic injunctions and prophetic
        traditions against Riba, we will see how adopting Bitcoin can empower
        Muslim societies to move toward an ethical, just, and sustainable
        financial system.
      </p>
      <h2>Test your understanding</h2>

      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicGuide64;
